/*
 * <sticky-add-to-cart> mirrors the product page's own add-to-cart button in a
 * bar fixed to the bottom of the screen, shown once that button scrolls out of
 * view. Clicking it clicks the real button (data-mode="add") or scrolls back to
 * the form (data-mode="scroll", used for bookings with required fields).
 */
if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // The bottle template already has its own sticky bar.
        if (document.querySelector('bottle-product-sticky')) return this.remove();

        this.mainButton = this.findMainButton();
        if (!this.mainButton) return this.remove();

        this.button = this.querySelector('.sticky-atc__button');
        this.price = this.querySelector('[data-sticky-price]');

        this.button.addEventListener('click', this.onClick.bind(this));
        this.syncButton();
        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled'],
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          // Only show once the real button has scrolled up and out of view.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast);
          this.setAttribute('aria-hidden', String(!scrolledPast));
          this.button.tabIndex = scrolledPast ? 0 : -1;
        });
        this.visibilityObserver.observe(this.mainButton);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data && event.data.variant;
            if (variant && this.price) this.price.textContent = this.formatMoney(variant.price);
            // The section re-renders on variant change, so re-find the button.
            this.rebind();
          });
        }
      }

      disconnectedCallback() {
        if (this.buttonObserver) this.buttonObserver.disconnect();
        if (this.visibilityObserver) this.visibilityObserver.disconnect();
        if (this.unsubscribe) this.unsubscribe();
      }

      findMainButton() {
        const main = document.getElementById('MainContent') || document;
        return (
          main.querySelector('form[action*="/cart/add"] button[type="submit"]:not([name="checkout"])') ||
          main.querySelector('button[name="add"]') ||
          main.querySelector('form[action*="/cart/add"] [type="submit"]')
        );
      }

      rebind() {
        const button = this.findMainButton();
        if (!button || button === this.mainButton) return;
        this.buttonObserver.disconnect();
        this.visibilityObserver.disconnect();
        this.mainButton = button;
        this.buttonObserver.observe(button, { attributes: true, childList: true, subtree: true, characterData: true });
        this.visibilityObserver.observe(button);
        this.syncButton();
      }

      syncButton() {
        this.button.disabled = this.mainButton.disabled;
        if (this.dataset.label) return;
        const text = (this.mainButton.querySelector('span') || this.mainButton).textContent.trim();
        if (text) this.button.textContent = text;
      }

      onClick() {
        if (this.dataset.mode === 'scroll') {
          const form = this.mainButton.closest('form') || this.mainButton;
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const firstField = form.querySelector('input:not([type="hidden"]), select, textarea');
          if (firstField) setTimeout(() => firstField.focus({ preventScroll: true }), 400);
          return;
        }
        this.mainButton.click();
      }

      formatMoney(cents) {
        return new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' }).format(
          cents / 100
        );
      }
    }
  );
}
